import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useAuth } from '../utils/AuthContext';
import { QUIZMASTER_API } from '../config';
import { formatToLocalDateTime, getUtcTimestamp } from '../utils/date';
import '../styles/styles.css';

const PAGE_SIZE = 10;

const statusOptions = [
    { label: 'Completed', value: 'Completed' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Not Started', value: 'Not Started' },
    { label: 'All statuses', value: 'all' },
];

const getStatusVariant = (status = '') => {
    const normalized = status.toLowerCase();
    if (normalized === 'completed') return 'completed';
    if (normalized === 'in_progress' || normalized === 'in progress') return 'in-progress';
    if (normalized === 'not_started' || normalized === 'not started') return 'pending';
    return 'default';
};

const ProfilePage = () => {
    const navigate = useNavigate();
    const { authState, refreshAccessToken } = useAuth();
    const { userName, userEmail, accessToken } = authState;

    const [quizInstances, setQuizInstances] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);
    const [statusFilter, setStatusFilter] = useState('Completed');
    const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
    const [loading, setLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [error, setError] = useState(null);
    const [documentMeta, setDocumentMeta] = useState({});

    const hasAuthToken = Boolean(accessToken);

    const userFullName = useMemo(() => {
        if (userName && userName.trim()) {
            return userName.trim();
        }
        if (userEmail) {
            return userEmail.split('@')[0];
        }
        return 'Name not provided';
    }, [userName, userEmail]);

    const userInitials = useMemo(() => {
        const nameParts = userFullName.split(/\s+/).filter(Boolean);
        if (nameParts.length === 0 || userFullName === 'Name not provided') {
            return (userEmail || '?').charAt(0).toUpperCase() || '?';
        }
        if (nameParts.length === 1) {
            return nameParts[0].charAt(0).toUpperCase();
        }
        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    }, [userFullName, userEmail]);

    const performAuthenticatedRequest = useCallback(
        async (url, options = {}) => {
            let token = accessToken;
            const requestInit = {
                ...options,
                headers: {
                    Accept: 'application/json',
                    ...(options.headers || {}),
                },
            };

            const makeRequest = async (bearer) =>
                fetch(url, {
                    ...requestInit,
                    headers: {
                        ...requestInit.headers,
                        Authorization: `Bearer ${bearer}`,
                    },
                });

            let response = await makeRequest(token);
            if (response.status === 401 && typeof refreshAccessToken === 'function') {
                try {
                    const newToken = await refreshAccessToken();
                    if (!newToken) {
                        throw new Error('Authentication required. Please sign in again.');
                    }
                    token = newToken;
                    response = await makeRequest(token);
                } catch (refreshError) {
                    throw new Error(refreshError.message || 'Authentication required. Please sign in again.');
                }
            }
            return response;
        },
        [accessToken, refreshAccessToken]
    );

    const loadQuizInstances = useCallback(async () => {
        if (!hasAuthToken) {
            setQuizInstances([]);
            setTotalPages(1);
            setTotalItems(0);
            setHasNext(false);
            setHasPrevious(false);
            return;
        }

        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
            page: `${page}`,
            page_size: `${PAGE_SIZE}`,
            order_by_desc: 'true',
        });

        if (statusFilter && statusFilter !== 'all') {
            params.append('status', statusFilter);
        }

        try {
            const response = await performAuthenticatedRequest(`${QUIZMASTER_API}/get-user-quiz-instances/?${params.toString()}`);
            if (!response.ok) {
                const message = response.status === 404 ? 'No quiz history found.' : 'Failed to load quiz history.';
                throw new Error(message);
            }
            const data = await response.json();
            setQuizInstances(Array.isArray(data.quiz_instances) ? data.quiz_instances : []);
            setTotalPages(data.total_pages || 1);
            setTotalItems(
                typeof data.total_items === 'number'
                    ? data.total_items
                    : (Array.isArray(data.quiz_instances) ? data.quiz_instances.length : 0)
            );
            setHasNext(Boolean(data.has_next));
            setHasPrevious(Boolean(data.has_previous));
            if (typeof data.page === 'number' && data.page >= 1 && data.page !== page) {
                setPage(data.page);
            }
        } catch (requestError) {
            setError(requestError.message || 'Failed to load quiz history.');
            setQuizInstances([]);
            setTotalPages(1);
            setTotalItems(0);
            setHasNext(false);
            setHasPrevious(false);
        } finally {
            setLoading(false);
        }
    }, [hasAuthToken, page, performAuthenticatedRequest, statusFilter]);

    useEffect(() => {
        loadQuizInstances();
    }, [loadQuizInstances]);

    useEffect(() => {
        const missingInstances = quizInstances.filter(
            (instance) => instance?.instance_id && !documentMeta[instance.instance_id]
        );

        if (missingInstances.length === 0) {
            return undefined;
        }

        let isCancelled = false;

        const fetchDocumentDetails = async () => {
            const updates = {};

            await Promise.all(
                missingInstances.map(async (instance) => {
                    try {
                        const response = await performAuthenticatedRequest(
                            `${QUIZMASTER_API}/get-quiz-instance-details/?instance_id=${instance.instance_id}`
                        );
                        if (!response.ok) {
                            return;
                        }
                        const data = await response.json();
                        const documentIdCandidate =
                            data.document_id ||
                            data.document?.id ||
                            data.document?.document_id ||
                            data.quiz?.document_id ||
                            null;

                        const documentNameCandidate =
                            data.document_title ||
                            data.document_filename ||
                            data.document_name ||
                            data.document?.title ||
                            data.document?.filename ||
                            data.document?.name ||
                            data.document?.original_filename ||
                            data.document?.metadata?.original_filename ||
                            data.document_details?.title ||
                            data.document_details?.filename ||
                            data.document_details?.name ||
                            data.document_details?.original_filename ||
                            data.quiz?.document_filename ||
                            data.quiz?.document_title ||
                            data.quiz?.document_name ||
                            instance.document_name ||
                            instance.document_title ||
                            instance.document_filename ||
                            instance.message ||
                            instance.quiz_type ||
                            'Untitled quiz';

                        const documentName = documentNameCandidate?.toString().trim() || 'Untitled quiz';
                        const documentId = documentIdCandidate ?? null;

                        updates[instance.instance_id] = {
                            documentId,
                            documentName,
                        };
                    } catch (fetchError) {
                        console.warn('[ProfilePage] Failed to resolve document name for instance', instance.instance_id, fetchError);
                    }
                })
            );

            if (!isCancelled && Object.keys(updates).length > 0) {
                setDocumentMeta((prev) => ({ ...prev, ...updates }));
            }
        };

        fetchDocumentDetails();

        return () => {
            isCancelled = true;
        };
    }, [quizInstances, documentMeta, performAuthenticatedRequest]);

    const handleStatusChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(1);
    };

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            const defaultDirection = key === 'updated_at' || key === 'score' ? 'desc' : 'asc';
            return { key, direction: defaultDirection };
        });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort size={12} />;
        return sortConfig.direction === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
    };

    const handlePageChange = (newPage) => {
        if (loading) return;
        if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
            setPage(newPage);
        }
    };

    const normalizedInstances = useMemo(() => {
        if (!Array.isArray(quizInstances)) return [];
        return quizInstances.map((instance, index) => {
            const normalizedStatus = (instance?.status || '').toLowerCase();
            const rawScore = instance?.score_percentage;
            const numericScore = rawScore === null || rawScore === undefined
                ? null
                : Number(rawScore);
            const score = Number.isFinite(numericScore) ? Math.round(numericScore) : null;
            const totalQuestions = Number.isFinite(Number(instance?.total_questions))
                ? Number(instance.total_questions)
                : (Array.isArray(instance?.results) ? instance.results.length : null);
            const totalCorrect = Number.isFinite(Number(instance?.total_correct))
                ? Number(instance.total_correct)
                : null;
            const meta = instance?.instance_id ? documentMeta[instance.instance_id] : undefined;
            const fallbackDocumentName =
                instance?.document_name ||
                instance?.document_title ||
                instance?.document_filename ||
                instance?.quiz_name ||
                instance?.quiz_type ||
                instance?.message ||
                'Untitled quiz';
            const resolvedDocumentName =
                (meta?.documentName || fallbackDocumentName || 'Untitled quiz')
                    ?.toString()
                    .trim() || 'Untitled quiz';
            const resolvedDocumentId = meta?.documentId ?? null;

            return {
                id: instance?.instance_id ?? `${instance?.quiz_id || 'quiz'}-${instance?.created_at || index}`,
                instanceId: instance?.instance_id ?? null,
                quizId: instance?.quiz_id ?? null,
                title: instance?.quiz_type || instance?.message || 'Quiz',
                status: instance?.status || 'Unknown',
                normalizedStatus,
                isCompleted: normalizedStatus === 'completed',
                isInProgress: normalizedStatus === 'in progress' || normalizedStatus === 'in_progress',
                score,
                totalQuestions,
                totalCorrect,
                createdAt: instance?.created_at || null,
                updatedAt: instance?.updated_at || instance?.created_at || null,
                documentName: resolvedDocumentName,
                documentId: resolvedDocumentId,
            };
        });
    }, [quizInstances, documentMeta]);

    const sortedInstances = useMemo(() => {
        const data = [...normalizedInstances];
        const { key, direction } = sortConfig;
        const multiplier = direction === 'asc' ? 1 : -1;

        const normalizeScore = (record) => {
            if (record.score === null || Number.isNaN(record.score)) {
                return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
            }
            return record.score;
        };

        data.sort((a, b) => {
            switch (key) {
                case 'status': {
                    const aStatus = a.status?.toLowerCase() || '';
                    const bStatus = b.status?.toLowerCase() || '';
                    return multiplier * aStatus.localeCompare(bStatus);
                }
                case 'score': {
                    const aScore = normalizeScore(a);
                    const bScore = normalizeScore(b);
                    return multiplier * (aScore - bScore);
                }
                case 'quiz_type': {
                    const aTitle = a.title?.toLowerCase() || '';
                    const bTitle = b.title?.toLowerCase() || '';
                    return multiplier * aTitle.localeCompare(bTitle);
                }
                case 'updated_at':
                default: {
                    const aDate = getUtcTimestamp(a.updatedAt ?? a.createdAt ?? 0) ?? 0;
                    const bDate = getUtcTimestamp(b.updatedAt ?? b.createdAt ?? 0) ?? 0;
                    return multiplier * (aDate - bDate);
                }
            }
        });

        return data;
    }, [normalizedInstances, sortConfig]);

    const monthlyTrend = useMemo(() => {
        const months = [];
        const now = new Date();
        for (let i = 4; i >= 0; i -= 1) {
            const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
            const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
            const label = date.toLocaleString(undefined, { month: 'short' });
            months.push({
                key,
                label,
                completed: 0,
                inProgress: 0,
            });
        }

        const monthBuckets = Object.fromEntries(months.map((month) => [month.key, month]));

        normalizedInstances.forEach((instance) => {
            const timestamp = getUtcTimestamp(instance.updatedAt ?? instance.createdAt);
            if (!timestamp) {
                return;
            }
            const occurrence = new Date(timestamp);
            const key = `${occurrence.getUTCFullYear()}-${String(occurrence.getUTCMonth() + 1).padStart(2, '0')}`;
            const bucket = monthBuckets[key];
            if (!bucket) {
                return;
            }
            if (instance.isCompleted) {
                bucket.completed += 1;
            } else if (instance.isInProgress) {
                bucket.inProgress += 1;
            }
        });

        return months;
    }, [normalizedInstances]);

    const chartSeries = useMemo(() => {
        const width = 240;
        const height = 80;
        const values = monthlyTrend.flatMap((month) => [month.completed, month.inProgress]);
        const rawMax = Math.max(0, ...values);
        const maxValue = rawMax > 0 ? rawMax : 1;
        const step = monthlyTrend.length > 1 ? width / (monthlyTrend.length - 1) : width;

        const toPoints = (selector) =>
            monthlyTrend.map((month, index) => {
                const value = selector(month);
                const x = monthlyTrend.length > 1 ? step * index : width / 2;
                const y = height - (value / maxValue) * height;
                return {
                    x,
                    y,
                    value,
                    label: month.label,
                };
            });

        return {
            width,
            height,
            completedPoints: toPoints((month) => month.completed),
            inProgressPoints: toPoints((month) => month.inProgress),
            labels: monthlyTrend.map((month) => month.label),
        };
    }, [monthlyTrend]);

    const documentBubbleData = useMemo(() => {
        const counts = new Map();
        normalizedInstances.forEach((instance) => {
            const label = (instance.documentName || instance.title || 'Untitled quiz')?.toString().trim() || 'Untitled quiz';
            const key = instance.documentId || label;

            if (counts.has(key)) {
                const entry = counts.get(key);
                entry.count += 1;
            } else {
                counts.set(key, { key, label, count: 1 });
            }
        });

        if (counts.size === 0) {
            return { items: [], hasData: false };
        }

        const items = Array.from(counts.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);

        const maxCount = items.reduce((max, item) => Math.max(max, item.count), 0) || 1;
        const minSize = 56;
        const maxSize = 110;

        const sizedItems = items.map((item, index) => {
            const ratio = item.count / maxCount;
            const size = Math.round(minSize + (maxSize - minSize) * ratio);
            const displayLabel = item.label.length > 20 ? `${item.label.slice(0, 17)}…` : item.label;
            return {
                ...item,
                size,
                accent: index % 3,
                displayLabel,
            };
        });

        return { items: sizedItems, hasData: true };
    }, [normalizedInstances]);

    const pageStats = useMemo(() => {
        const completedOnPage = sortedInstances.filter((item) => item.isCompleted).length;
        const inProgressOnPage = sortedInstances.filter((item) => item.isInProgress).length;
        const scoreValues = sortedInstances
            .map((item) => item.score)
            .filter((value) => value !== null && Number.isFinite(value));
        const averageScore = scoreValues.length > 0
            ? Math.round((scoreValues.reduce((sum, value) => sum + value, 0) / scoreValues.length) * 10) / 10
            : null;
        const lastActivityTimestamp = sortedInstances.reduce((latest, item) => {
            const ts = getUtcTimestamp(item.updatedAt ?? item.createdAt);
            if (ts !== null && ts > latest) {
                return ts;
            }
            return latest;
        }, 0);

        return {
            completedOnPage,
            inProgressOnPage,
            averageScore,
            lastActivityTimestamp: lastActivityTimestamp || null,
        };
    }, [sortedInstances]);

    const statusSummary = useMemo(() => {
        if (!totalItems) {
            return 'No quiz attempts yet.';
        }
        return statusFilter === 'all'
            ? `${totalItems} quiz ${totalItems === 1 ? 'attempt' : 'attempts'} found`
            : `${totalItems} ${statusFilter.toLowerCase()} ${totalItems === 1 ? 'attempt' : 'attempts'} found`;
    }, [statusFilter, totalItems]);

    const lastActivityLabel = pageStats.lastActivityTimestamp
        ? formatToLocalDateTime(pageStats.lastActivityTimestamp, {
            fallback: 'No activity yet',
            includeSeconds: false,
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZoneName: null,
        })
        : 'No activity yet';

    const handleViewResults = useCallback((instance) => {
        if (!instance?.instanceId) return;
        navigate('/results', { state: { selectedInstanceId: instance.instanceId } });
    }, [navigate]);

    const handleResumeQuiz = useCallback((instance) => {
        if (!instance?.instanceId) return;
        navigate('/quiz', { state: { selectedInstanceId: instance.instanceId } });
    }, [navigate]);

    const handleStartNewInstance = useCallback(async (instance) => {
        if (!instance?.quizId) {
            setError('Unable to start a new quiz instance for this quiz.');
            return;
        }
        setActionLoadingId(instance.instanceId);
        setError(null);
        try {
            const response = await performAuthenticatedRequest(
                `${QUIZMASTER_API}/create-quiz-instance/?quiz_id=${instance.quizId}`,
                { method: 'POST' }
            );
            if (!response.ok) {
                throw new Error('Failed to create a new quiz instance. Please try again.');
            }
            const data = await response.json();
            if (!data?.instance_id) {
                throw new Error('Quiz instance created but no identifier returned.');
            }
            navigate('/quiz', { state: { selectedInstanceId: data.instance_id } });
        } catch (requestError) {
            setError(requestError.message || 'Failed to create a new quiz instance.');
        } finally {
            setActionLoadingId(null);
        }
    }, [performAuthenticatedRequest, navigate]);

    const handleRefresh = () => {
        loadQuizInstances();
    };

    const averageScoreLabel = pageStats.averageScore !== null
        ? `${pageStats.averageScore}%`
        : '—';

    return (
        <div className="home-landing profile-landing">
            <section className="home-landing__hero profile-landing__hero">
                <div className="home-landing__hero-content profile-landing__hero-content">
                    <div className="profile-landing__identity">
                        <div className="profile-landing__headline">
                            <div className="profile-landing__avatar">{userInitials}</div>
                            <div className="profile-landing__headline-text">
                                <div className="profile-landing__identity-meta">
                                    <span className="profile-landing__identity-name">{userFullName}</span>
                                    <span className="profile-landing__identity-email">{userEmail || 'Email not provided'}</span>
                                </div>
                            </div>
                        </div>
                        <p className="home-landing__subtitle">
                            Here&apos;s a quick snapshot of your quiz journey. Dive into past results, resume active quizzes, or start fresh in seconds.
                        </p>
                    </div>
                    <div className="profile-landing__stats">
                        <div className="profile-landing__stat-card profile-landing__stat-card--chart">
                            <div className="profile-landing__stat-header">
                                <span className="profile-landing__stat-label">Last 5 months</span>
                                <span className="profile-landing__stat-helper">Completed vs in progress</span>
                            </div>
                            <div className="profile-landing__chart-wrapper">
                                <svg
                                    className="profile-landing__chart"
                                    viewBox={`0 0 ${chartSeries.width} ${chartSeries.height}`}
                                    width={chartSeries.width}
                                    height={chartSeries.height}
                                    role="img"
                                    aria-label="Line chart showing completed and in-progress quizzes for the last five months"
                                >
                                    <polyline
                                        className="profile-landing__chart-line profile-landing__chart-line--completed"
                                        points={chartSeries.completedPoints.map((point) => `${point.x},${point.y}`).join(' ')}
                                        fill="none"
                                    />
                                    <polyline
                                        className="profile-landing__chart-line profile-landing__chart-line--in-progress"
                                        points={chartSeries.inProgressPoints.map((point) => `${point.x},${point.y}`).join(' ')}
                                        fill="none"
                                    />
                                    {chartSeries.completedPoints.map((point) => (
                                        <circle
                                            key={`completed-${point.label}`}
                                            cx={point.x}
                                            cy={point.y}
                                            r={2.5}
                                            className="profile-landing__chart-dot profile-landing__chart-dot--completed"
                                        />
                                    ))}
                                    {chartSeries.inProgressPoints.map((point) => (
                                        <circle
                                            key={`inprogress-${point.label}`}
                                            cx={point.x}
                                            cy={point.y}
                                            r={2.5}
                                            className="profile-landing__chart-dot profile-landing__chart-dot--in-progress"
                                        />
                                    ))}
                                </svg>
                                <div className="profile-landing__chart-axis" aria-hidden="true">
                                    {chartSeries.labels.map((label) => (
                                        <span key={`axis-${label}`} className="profile-landing__chart-axis-label">
                                            {label}
                                        </span>
                                    ))}
                                </div>
                                <div className="profile-landing__chart-legend" aria-hidden="true">
                                    <span className="profile-landing__legend-item">
                                        <span className="profile-landing__legend-dot profile-landing__legend-dot--completed" />
                                        Completed
                                    </span>
                                    <span className="profile-landing__legend-item">
                                        <span className="profile-landing__legend-dot profile-landing__legend-dot--in-progress" />
                                        In progress
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="profile-landing__stat-card">
                            <span className="profile-landing__stat-label">Average score</span>
                            <span className="profile-landing__stat-value">{averageScoreLabel}</span>
                            <span className="profile-landing__stat-helper">Calculated from attempts in view</span>
                        </div>
                        <div className="profile-landing__stat-card">
                            <span className="profile-landing__stat-label">Last activity</span>
                            <span className="profile-landing__stat-value profile-landing__stat-value--compact">{lastActivityLabel}</span>
                            <span className="profile-landing__stat-helper">{pageStats.inProgressOnPage} quiz{pageStats.inProgressOnPage === 1 ? '' : 'zes'} in progress here</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="home-landing__recent profile-landing__history">
                <div className="home-landing__recent-header profile-landing__history-header">
                    <div>
                        <h2 className="home-landing__recent-title">Quiz History</h2>
                        <p className="profile-landing__subtitle">{statusSummary}</p>
                    </div>
                    <button
                        type="button"
                        className="button button--ghost profile-landing__refresh"
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        {loading ? 'Refreshing…' : 'Refresh list'}
                    </button>
                </div>

                <div className="profile-landing__filters">
                    <div className="profile-landing__filter-group">
                        <label htmlFor="status-filter" className="profile-landing__filter-label">Filter by status</label>
                        <select
                            id="status-filter"
                            className="profile-landing__select"
                            value={statusFilter}
                            onChange={handleStatusChange}
                            disabled={loading}
                        >
                            {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="document-sort profile-landing__sort">
                        <span className="document-sort__label">Sort by</span>
                        <button
                            type="button"
                            className="document-sort-button"
                            onClick={() => handleSort('updated_at')}
                            aria-pressed={sortConfig.key === 'updated_at'}
                        >
                            Most recent {getSortIcon('updated_at')}
                        </button>
                        <button
                            type="button"
                            className="document-sort-button"
                            onClick={() => handleSort('score')}
                            aria-pressed={sortConfig.key === 'score'}
                        >
                            Score {getSortIcon('score')}
                        </button>
                        <button
                            type="button"
                            className="document-sort-button"
                            onClick={() => handleSort('status')}
                            aria-pressed={sortConfig.key === 'status'}
                        >
                            Status {getSortIcon('status')}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="home-landing__state home-landing__state--error">
                        <p>{error}</p>
                        <button type="button" className="button" onClick={handleRefresh} disabled={loading}>
                            Try again
                        </button>
                    </div>
                )}

                {!error && (
                    <>
                        {loading && sortedInstances.length === 0 ? (
                            <div className="home-landing__state">
                                <p>Loading quiz history…</p>
                            </div>
                        ) : sortedInstances.length === 0 ? (
                            <div className="home-landing__state">
                                <p>No quiz attempts to show yet. Complete a quiz to see it listed here.</p>
                            </div>
                        ) : (
                            <div className="home-landing__list profile-landing__grid">
                                {sortedInstances.map((instance) => {
                                    const statusVariant = getStatusVariant(instance.status);
                                    const statusClass = `profile-status-pill profile-status-pill--${statusVariant}`;
                                    const scoreLabel = instance.score !== null ? `${instance.score}%` : 'Pending';
                                    const questionsLabel = instance.totalQuestions !== null
                                        ? `${instance.totalCorrect ?? '—'} / ${instance.totalQuestions}`
                                        : 'N/A';
                                    const startedLabel = formatToLocalDateTime(instance.createdAt, {
                                        fallback: 'Unknown',
                                        includeSeconds: false,
                                        dateStyle: 'medium',
                                        timeStyle: 'short',
                                        timeZoneName: null,
                                    });
                                    const actionLabel = instance.isCompleted
                                        ? 'View results'
                                        : instance.isInProgress
                                            ? 'Resume quiz'
                                            : 'Take quiz';

                                    return (
                                        <div key={instance.id} className="home-landing__card profile-landing__card">
                                            <div className="profile-landing__card-title">
                                                <h3 className="home-landing__card-title">{instance.title}</h3>
                                                <span className={statusClass}>{instance.status}</span>
                                            </div>
                                            <div className="profile-landing__card-metrics">
                                                <div className="profile-landing__metric">
                                                    <span className="profile-landing__metric-label">Instance</span>
                                                    <span className="profile-landing__metric-value">#{instance.instanceId || 'N/A'}</span>
                                                </div>
                                                <div className="profile-landing__metric">
                                                    <span className="profile-landing__metric-label">Score</span>
                                                    <span className="profile-landing__metric-value">{scoreLabel}</span>
                                                </div>
                                                <div className="profile-landing__metric">
                                                    <span className="profile-landing__metric-label">Questions</span>
                                                    <span className="profile-landing__metric-value">{questionsLabel}</span>
                                                </div>
                                                <div className="profile-landing__metric">
                                                    <span className="profile-landing__metric-label">Started</span>
                                                    <span className="profile-landing__metric-value">{startedLabel}</span>
                                                </div>
                                            </div>
                                            <div className="profile-landing__card-actions">
                                                {instance.isCompleted ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="button button--ghost"
                                                            onClick={() => handleViewResults(instance)}
                                                        >
                                                            {actionLabel}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="button"
                                                            onClick={() => handleStartNewInstance(instance)}
                                                            disabled={actionLoadingId === instance.instanceId || loading}
                                                        >
                                                            {actionLoadingId === instance.instanceId ? 'Preparing…' : 'Generate new quiz'}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="button"
                                                        onClick={() => handleResumeQuiz(instance)}
                                                    >
                                                        {actionLabel}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <button
                                    type="button"
                                    className="button pagination-button"
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={!hasPrevious || loading || page <= 1}
                                >
                                    Previous
                                </button>
                                <span className="page-info">
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    type="button"
                                    className="button pagination-button"
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={!hasNext || loading || page >= totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </section>
        </div>
    );
};

export default ProfilePage;
